import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldX, ArrowLeft } from 'lucide-react';
import { usePermissions, PAGE_SECTION_MAP, PAGE_ROLE_MAP, SECTION_LABELS } from '../../hooks/usePermissions';
import { useAuth } from '../../context/AuthContext';

export default function PermissionGate({ path, section: sectionProp, children }) {
  const { user } = useAuth();
  const { can, loading } = usePermissions();
  const navigate = useNavigate();
  const redirectedRef = useRef(false);

  const FULL_ACCESS = ['super_admin', 'admin'];
  const isFullAccess = FULL_ACCESS.includes(user?.role);
  const section = sectionProp || PAGE_SECTION_MAP[path] || null;
  const roleRestricted = PAGE_ROLE_MAP[path];
  const roleDenied = !loading && !!roleRestricted && !roleRestricted.includes(user?.role);

  // Only compute denial after loading is done
  const denied = roleDenied || (!loading && !isFullAccess && section && !can(section, 'can_view'));

  useEffect(() => {
    if (denied && !redirectedRef.current) {
      redirectedRef.current = true;
      navigate('/admin', { replace: true });
    }
  }, [denied, navigate]);

  // While loading OR full access OR no section → render children immediately (no flash/spinner)
  if (!denied) return children;

  // Show blocked screen while redirect is happening
  const sectionLabel = roleDenied ? 'this page' : (SECTION_LABELS[section] || section || 'this section');
  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      flexDirection: 'column', gap: 20, background: '#050508', padding: 20, textAlign: 'center'
    }}>
      <div style={{ width: 72, height: 72, borderRadius: 20, background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <ShieldX size={32} color="#ef4444" />
      </div>
      <div>
        <h2 style={{ color: '#fff', fontSize: 22, fontWeight: 800, margin: '0 0 8px' }}>Access Denied</h2>
        <p style={{ color: '#475569', fontSize: 14, margin: 0, maxWidth: 360 }}>
          Your role <strong style={{ color: '#818cf8' }}>{user?.role?.replace(/_/g, ' ')}</strong> cannot access <strong style={{ color: '#e2e8f0' }}>{sectionLabel}</strong>.
        </p>
        <p style={{ color: '#334155', fontSize: 12, margin: '8px 0 0' }}>Contact your super admin to request access.</p>
      </div>
      <button onClick={() => navigate('/admin', { replace: true })}
        style={{ background: 'rgba(99,102,241,0.15)', color: '#818cf8', border: '1px solid rgba(99,102,241,0.25)', borderRadius: 12, padding: '10px 22px', cursor: 'pointer', fontWeight: 600, fontSize: 13, display: 'flex', alignItems: 'center', gap: 8 }}>
        <ArrowLeft size={15} /> Go to Dashboard
      </button>
    </div>
  );
}
